import { Capacitor } from '@capacitor/core'
import { supabase } from '../lib/supabase'
import { kaufweg } from '../lib/kaufweg'
import { StaySuiteStore, type StoreKauf, type StoreProdukt } from '../lib/staysuiteStore'

/**
 * EIN Modul für beide Läden (ADR 009, „Anwendung") — heute trägt es die
 * Apple-Hälfte (Vorgang 009, Schnitt 8). Die Google-Seite ist zurückgestellt
 * (Renés Entscheid 19.08.2026) und kommt formgleich hier hinein, nicht daneben.
 *
 * Der Server entscheidet über die persistierte Wirkung. Beenden darf der Client
 * ausschließlich die exakt vom Server bestätigte signierte Transaktion.
 * Beobachtung, Konflikte und Fehlermeldungen lassen StoreKits Wiederholung offen.
 */

/*
 * Die drei Produkt-IDs aus App Store Connect (App 6801594983, Gruppe
 * „StaySuite Abo" 22319077, angelegt 19.08.2026). IDs sind keine Preise —
 * der Betrag kommt ausschließlich aus dem Laden (`displayPrice`, AK32);
 * die Zuordnung Produkt → Paket liegt in `abo_laden_produkte`, nicht hier.
 */
export const APPLE_PRODUKT_IDS = [
  'staysuite_paket1_monthly',
  'staysuite_paket2_monthly',
  'staysuite_paket3_monthly',
] as const

export interface LadenPreisMeldung {
  storefront: string // dreibuchstabig, wie StoreKit ihn liefert; AT-Schlüssel macht der Server (A8.5)
  waehrung: string
  betrag: number // Ganzzahl in der kleinsten Einheit
  produkt_id: string
}

/**
 * Zeigt dieser Build das Laden-Kaufblatt überhaupt an? Zwei Wege hinein:
 * der live geschaltete Kaufweg (`kaufweg() === 'apple'`, fällt mit A8.6/A8.7)
 * oder der Sandkasten-Prüfbuild (VITE_STORE_SANDBOX bzw. DEV — dieselbe
 * Trennung wie beim Android-Schnitt 1: der gewöhnliche Produktionsbuild setzt
 * beides nie). Dazu kommt zur Laufzeit der Serverschalter `kaufwegOffen()`.
 */
export function ladenKaufwegSichtbar(): boolean {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return false
  if (kaufweg() === 'apple') return true
  return import.meta.env.DEV || import.meta.env.VITE_STORE_SANDBOX === '1'
}

/**
 * Der serverseitige Rückzugsschalter (X9/A8.6): nur ein ausdrückliches `true`
 * öffnet den Kaufweg. Jeder Fehler — kein Backend, RPC fehlt, Netz weg —
 * heißt geschlossen. Fail closed ist hier die einzige richtige Richtung:
 * ein Kaufknopf, der wegen eines Fehlers erscheint, ist nicht zurückholbar.
 */
export async function kaufwegOffen(): Promise<boolean> {
  if (!supabase) return false
  try {
    const { data, error } = await supabase.rpc('kaufweg_offen')
    if (error) return false
    return data === true
  } catch {
    return false
  }
}

export interface LadenProdukte {
  produkte: StoreProdukt[]
  /** Länderkodex der Storefront, dreibuchstabig (`AUT`) — für die Preismeldung. */
  storefront: string | null
}

export async function ladenProdukte(): Promise<LadenProdukte> {
  const antwort = await StaySuiteStore.produkte({ produktIds: [...APPLE_PRODUKT_IDS] })
  return { produkte: antwort.produkte ?? [], storefront: antwort.storefront ?? null }
}

/** NUR der Betrag des Ladens — Apples lokalisierte Anzeige, nie eine Zahl aus `src/` (AK32). */
export function ladenPreis(p: StoreProdukt): string {
  return p.formattedPrice
}

export type LadenZustand = 'active' | 'observed' | 'pending' | 'cancelled' | 'failed'
export interface LadenKaufErgebnis {
  kauf: StoreKauf | null
  server: Record<string, unknown> | null
  fehler: string | null
  zustand: LadenZustand
}

/** Nur eine serverbestätigte Wirkung für genau diesen Nachweis darf finish auslösen. */
export function transaktionDarfBeendetWerden(kauf: StoreKauf, server: Record<string, unknown>): boolean {
  return server.ok === true && server.finish_erlaubt === true &&
    server.modus !== 'off' && ['geschrieben', 'unveraendert', 'rueckkehr_aus_rettung'].includes(String(server.ergebnis)) &&
    !!kauf.bestellnummer && server.transaktion_id === kauf.bestellnummer &&
    server.produkt_id === kauf.produktId && server.griff === kauf.purchaseToken
}

const laufendeMeldungen = new Map<string, Promise<LadenKaufErgebnis>>()
function kaufMelden(tenantId: string, kauf: StoreKauf): Promise<LadenKaufErgebnis> {
  const schluessel = `${tenantId}:${kauf.jws}`
  const laufend = laufendeMeldungen.get(schluessel)
  if (laufend) return laufend
  const auftrag = kaufMeldenEinmal(tenantId, kauf).finally(() => laufendeMeldungen.delete(schluessel))
  laufendeMeldungen.set(schluessel, auftrag)
  return auftrag
}

async function kaufMeldenEinmal(tenantId: string, kauf: StoreKauf): Promise<LadenKaufErgebnis> {
  if (!kauf.jws) return { kauf, server: null, fehler: 'nachweis_fehlt', zustand: 'failed' }
  const server = await meldeTransaktion(tenantId, kauf.jws)
  if (!server || server.ok !== true) {
    return { kauf, server, fehler: String(server?.error ?? 'server_meldung_fehlgeschlagen'), zustand: 'failed' }
  }
  if (transaktionDarfBeendetWerden(kauf, server)) {
    try {
      await StaySuiteStore.transaktionBeenden({ transaktionId: kauf.bestellnummer!, jws: kauf.jws })
      return { kauf, server, fehler: null, zustand: 'active' }
    } catch {
      return { kauf, server, fehler: 'finish_fehlgeschlagen', zustand: 'failed' }
    }
  }
  if (server.ergebnis === 'beobachtet' && server.modus === 'off') {
    return { kauf, server, fehler: null, zustand: 'observed' }
  }
  return { kauf, server, fehler: String(server.ergebnis ?? 'nicht_angewendet'), zustand: 'failed' }
}

export async function ladenKaufen(produktId: string, tenantId: string): Promise<LadenKaufErgebnis> {
  // Serverprüfung VOR dem kostenpflichtigen Apple-Blatt, insbesondere Paketgröße und Inhaber.
  if (!supabase) return { kauf: null, server: null, fehler: 'kaufweg_geschlossen', zustand: 'failed' }
  const { data: erlaubt, error } = await supabase.rpc('laden_kauf_vorbereiten', {
    p_tenant: tenantId, p_produkt: produktId,
  })
  if (error || erlaubt !== true) return { kauf: null, server: null, fehler: 'kaufweg_geschlossen', zustand: 'failed' }
  const antwort = await StaySuiteStore.kaufen({ produktId, basisplanId: 'monthly', kontoKennung: tenantId })
  const kauf = antwort.kaeufe?.[0] ?? null
  if (antwort.responseCode !== 0 || !kauf?.jws) {
    const zustand = antwort.responseCode === 1 ? 'cancelled' : antwort.debugMessage === 'pending' ? 'pending' : 'failed'
    return { kauf, server: null, fehler: zustand === 'failed' ? 'kauf_nicht_abgeschlossen' : null, zustand }
  }
  return kaufMelden(tenantId, kauf)
}

export interface LadenAbgleichErgebnis {
  zustand: 'active' | 'observed' | 'empty' | 'failed'
  ergebnisse: LadenKaufErgebnis[]
}

/** Hintergrund ohne Prompt; nur der Wiederherstellen-Klick setzt interaktiv=true. */
export async function ladenAnspruchMelden(
  tenantId: string,
  preisMeldungen?: LadenPreisMeldung[],
  interaktiv = false,
): Promise<LadenAbgleichErgebnis> {
  try {
    // Preistelemetrie blockiert die Wiederherstellung nicht, ist aber unabhängig
    // vom Anspruch und darf einen fehlgeschlagenen Anspruch niemals grün färben.
    try {
      let meldungen = preisMeldungen
      if (!meldungen) {
        const { produkte, storefront } = await ladenProdukte()
        const mikroJeCent = 10_000
        meldungen = storefront ? produkte.map((p) => ({ storefront, waehrung: p.waehrung,
          betrag: Math.round(p.betragMikro / mikroJeCent), produkt_id: p.produktId })) : []
      }
      for (const m of meldungen) {
        await supabase?.functions.invoke('apple-purchase', { body: { tenant_id: tenantId, laden_preis: m } })
      }
    } catch { /* Anspruch weiterhin unabhängig abgleichen. */ }
    const antwort = await StaySuiteStore.aktiveKaeufe({ interaktiv })
    if (antwort.responseCode !== 0) return { zustand: 'failed', ergebnisse: [] }
    const ergebnisse: LadenKaufErgebnis[] = []
    for (const kauf of antwort.kaeufe ?? []) ergebnisse.push(await kaufMelden(tenantId, kauf))
    return { ergebnisse, zustand: ergebnisse.some((e) => e.zustand === 'failed') ? 'failed'
      : ergebnisse.some((e) => e.zustand === 'active') ? 'active'
      : ergebnisse.length ? 'observed' : 'empty' }
  } catch {
    return { zustand: 'failed', ergebnisse: [] }
  }
}

/** Pending/Familienfreigabe wird während der laufenden App erneut serverseitig geprüft. */
export function ladenAenderungenBeobachten(
  tenantId: string,
  aktualisiert: (ergebnis: LadenKaufErgebnis) => void,
): Promise<{ remove: () => Promise<void> }> {
  return StaySuiteStore.addListener('kaufAktualisiert', (kauf) => {
    void kaufMelden(tenantId, kauf).then(aktualisiert).catch(() => {
      aktualisiert({ kauf, server: null, zustand: 'failed', fehler: 'server_meldung_fehlgeschlagen' })
    })
  })
}

/** Apples Verwaltungsblatt (Kündigung, Paketwechsel) — siehe Plugin-Kommentar. */
export async function ladenAboVerwalten(): Promise<void> {
  await StaySuiteStore.aboVerwalten()
}

async function meldeTransaktion(
  tenantId: string,
  jws: string,
): Promise<Record<string, unknown> | null> {
  if (!supabase) return null
  const { data, error } = await supabase.functions.invoke('apple-purchase', {
    body: { tenant_id: tenantId, jws },
  })
  if (error) {
    // Die benannten Gründe (`gehoert_anderem_betrieb`, `keine_berechtigung`, …)
    // stecken im Antwortrumpf hinter dem generischen invoke-Fehler.
    const res = (error as { context?: Response }).context
    if (res) {
      try {
        return (await res.json()) as Record<string, unknown>
      } catch {
        return null
      }
    }
    return null
  }
  return (data ?? null) as Record<string, unknown> | null
}
